import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { getContentKeyAccToLang } from '../../../utils/utils';
import { getCorpKycGeneralInfoDropdowns, getKycTaxCertification, getTaxSelfCertification, getTaxSelfCertificationDropdowns, saveKycTaxCertification, saveTaxSelfCertification } from '../../../store/actions/corporateAction';
import { ControllingPersonsNullData, international_tax_self_certification_validation_schema } from '../../../validations_schemas/international_tax_self_certification_schema';
import { ActiveNonFinancial, Checkbox, Chips, ErrorText, ExemptEntityThen, FIThen, NonReportingFiThen, NonUsFinancialThen, NonUsNotFinancialThen, PassiveNonFinancialEntity, PassiveNonUsEntity, RadioButton, RowView, Text, UsEntitiesFinancialInstitutions, ViewItems } from '../components';
import CustomCheckbox from '../../../components/CustomCheckbox';
import Dropdown from '../../../components/Dropdown';
import Button from '../../../components/Button';
import { fontSizes } from '../../../constants/constants';
import { useNavigate } from 'react-router';
import { formatGIIN } from '../../../utils/functions';
import ControllingPerson from '../components/ControllingPerson/controlling_person';
import CustomInput from '../../../components/CustomInput';
import { logoutUser } from '../../../store/actions/authActions';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const InternationalTaxSelfCertification = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic, TextWeight } = dynamicfunction();
  const { corporate, language } = useSelector((state) => state.content);

  const getContent = (key) => {
    return getContentKeyAccToLang(corporate?.corporate_international_tax_self_certification?.data?.attributes, key, isArabic);
  };
  const { text_self_certification_dropdowns, corporate_post_loading, tax_self_certification_data, general_information_dropdowns } = useSelector(
    (state) => state.corporate
  );
  const {
    client_application_id,
    is_kyc,
  } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const findCountry = (id) => general_information_dropdowns?.countries?.find((item) => item?.id === id) || null;

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(is_kyc ? getKycTaxCertification(client_application_id) : getTaxSelfCertification(client_application_id));
    await dispatch(getTaxSelfCertificationDropdowns());
    await dispatch(getCorpKycGeneralInfoDropdowns());
  }

  const onSubmit = (values) => {
    const shouldIncludeControllingPersons =
      (Number(values?.crsClassificationId) === 1463 && Number(values?.financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry) === 1) ||
      (Number(values?.crsClassificationId) === 1465 && Number(values?.passiveNfe) === 1) ||
      (Number(values?.fatcaClassificationTinId) === 1462 && Number(values?.passiveNonFinancialNonUsEntity) === 1);

    const ControllingPersons = {
      firstNameOfControllingPersons: values?.firstNameOfControllingPersons,
      crsClassificationAddressOfControllingPerson: values?.crsClassificationAddressOfControllingPerson,
      middleNameOfControllingPersons: values?.middleNameOfControllingPersons,
      lastNameOfControllingPersons: values?.lastNameOfControllingPersons,
      cityOfControllingPersons: values?.cityOfControllingPersons,
      countryOfControllingPersonsId: values?.countryOfControllingPersons?.id,
      districtOfControllingPersons: values?.districtOfControllingPersons,
      buildingNumberOfControllingPersons: values?.buildingNumberOfControllingPersons,
      streetNameOfControllingPersons: values?.streetNameOfControllingPersons,
      mailingAddressOfControllingPersons: values?.mailingAddressOfControllingPersons,
      crsClassificationNationalityOfControllingPerson: values?.crsClassificationNationalityOfControllingPerson,
      crsClassificationDateOfBirth: values?.crsClassificationDateOfBirth,
      crsClassificationPlaceOfBirth: values?.crsClassificationPlaceOfBirth,
      countryOfTaxResidenceOfControllingPersons: values?.countryOfTaxResidenceOfControllingPersons,
      crsClassificationPercentageOfOwnerShip: values?.crsClassificationPercentageOfOwnerShip,
      controllingPersonStatusId: values?.controllingPersonStatusId?.[0]?.valueId,
      zipcodeOfControllingPersons: values?.zipcodeOfControllingPersons,
      additionalNoOfControllingPersons: values?.additionalNoOfControllingPersons,
      tinForControllingPerson: values?.tinForControllingPerson,
      unavailbleTINControllingPersonReasonId: values?.unavailbleTINControllingPersonReasonId?.[0]?.valueId,
      reasonForTINUnavailableForControllingPerson: values?.reasonForTINUnavailableForControllingPerson,
    };
    const api_data = {
      inputs: {
        ...tax_self_certification_data?.inputs,
        ...(shouldIncludeControllingPersons ? ControllingPersons : ControllingPersonsNullData),
        isAgreeFatcaAndCrs: values?.isAgreeFatcaAndCrs,
        fullLegalNameOfTheEntity: values?.fullLegalNameOfTheEntity,
        city: values?.city,
        // country: values?.country,
        countryId: values?.country?.id,
        // currentResidenceCountry: values?.currentResidenceCountry,
        currentResidenceCountryId: values?.currentResidenceCountry?.id,
        currentResidenceCity: values?.currentResidenceCity,
        currentResidenceDistrict: values?.currentResidenceDistrict,
        currentResidenceBuildingNumber: values?.currentResidenceBuildingNumber,
        currentResidenceStreetName: values?.currentResidenceStreetName,
        currentResidencePostalCode: values?.currentResidencePostalCode,
        corpInternationalTaxSelfCertificationOfficeAddressesList: [
          {
            buildingNumber: values?.mailingbuildingNumber,
            streetName: values?.mailingstreetName,
            district: values?.mailingdistrict,
            city: values?.mailingcity,
            postalCode: values?.mailingpostalCode,
            // country: values?.mailingcountry,
            countryId: values?.mailingcountry?.id,
          },
        ],
        corpInternationalTaxSelfCertificationTaxResidenceInformationList: [
          {
            countryJurisdiction: values?.taxresidenceCountryJurisdiction,
            taxIdentificationNumber: values?.taxresidencetaxIdentificationNumber,
          },
        ],
        taxResidenceNoTinReasonId: values.taxResidenceNoTinReasonId?.[0]?.valueId,
        taxResidenceInfoReasonForNoTin: values?.taxResidenceInfoReasonForNoTin,
        fatcaClassificationTinId: values?.fatcaClassificationTinId,
        fatcaClassificationIsSpecifiedUsPerson: values?.fatcaClassificationIsSpecifiedUsPerson?.toString() === '1' ? true : false,
        usPersonWhoIsNotASpecifiedUsPerson: values?.usPersonWhoIsNotASpecifiedUsPerson?.toString() === '1' ? true : false,
        crsClassificationId: values?.crsClassificationId,
        depositoryInstitution: values?.depositoryInstitution?.toString() === '1' ? true : false,
        custodialInstitution: values?.custodialInstitution?.toString() === '1' ? true : false,
        specifiedInsuranceCompany: values?.specifiedInsuranceCompany?.toString() === '1' ? true : false,
        anInvestmentEntityGrossIncomePrimarily: values?.anInvestmentEntityGrossIncomePrimarily?.toString() === '1' ? true : false,
        anInvestmentEntityTypeA: values?.investmentEntityTypes?.toString() === '1' ? true : null,
        anInvestmentEntityTypeB: values?.investmentEntityTypes?.toString() === '0' ? true : null,
        corpClientApplicationInternationalTaxSelfCertificationId: 0,
        corpClientApplicationId: client_application_id,
        financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry:
          values?.financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry?.toString() === '1' ? true : false,
        isConfirmTheAboveInformation: values?.isConfirmTheAboveInformation,
        fatcaClassificationUsTinNumber: values?.fatcaClassificationUsTinNumber,
        ksaFinancialInstitution: values?.ksaFinancialInstitution?.toString() === '1' ? true : false,
        GiinKsaFinancialInstitution: values?.giinKsaFinancialInstitution?.replaceAll('-', '') ?? '',
        ReasonProvidedForNotHavingGiin: values?.reasonProvidedForNotHavingGiin,
        ParticipatingFinancialInstitution: values?.participatingFinancialInstitution?.toString() === '1' ? true : false,
        GiinParticipatingFinancialInstitution: values?.giinParticipatingFinancialInstitution?.replaceAll('-', '') ?? '',
        RegisteredDeemedCompliantFinancialInstitution: values?.registeredDeemedCompliantFinancialInstitution?.toString() === '1' ? true : false,
        LocalFfi: values?.localFfi?.toString() === '1' ? true : false,
        NonReportingMemberPffiGroup: values?.nonReportingMemberPffiGroup?.toString() === '1' ? true : false,
        QualifiedCollectiveInvestmentVehicles: values?.qualifiedCollectiveInvestmentVehicles?.toString() === '1' ? true : false,
        RestrictedFunds: values?.restrictedFunds?.toString() === '1' ? true : false,
        QualifiedCreditCardIssuers: values?.qualifiedCreditCardIssuers?.toString() === '1' ? true : false,
        SponsoredInvestmentEntities: values?.sponsoredInvestmentEntities?.toString() === '1' ? true : false,
        SponsoredControlledForeignCorporation: values?.sponsoredControlledForeignCorporation?.toString() === '1' ? true : false,
        GiinSponsoredControlledForeignCorporation: values?.giinSponsoredControlledForeignCorporation?.replaceAll('-', '') ?? '',
        CertifiedDeemedCompliantFinancialInstitution: values?.certifiedDeemedCompliantFinancialInstitution?.toString() === '1' ? true : false,
        NonRegisteringLocalBank: values?.nonRegisteringLocalBank?.toString() === '1' ? true : false,
        FfiWithOnlyLowValueAccounts: values?.ffiWithOnlyLowValueAccounts?.toString() === '1' ? true : false,
        SponsoredCloselyHeldInvestmentVehicles: values?.sponsoredCloselyHeldInvestmentVehicles?.toString() === '1' ? true : false,
        LimitedLifeDebtInvestmentEntities: values?.limitedLifeDebtInvestmentEntities?.toString() === '1' ? true : false,
        InvestmentAdvisorsAndInvestmentManagers: values?.investmentAdvisorsAndInvestmentManagers?.toString() === '1' ? true : false,
        OwnerDocumentedFfis: values?.ownerDocumentedFfis?.toString() === '1' ? true : false,
        NonParticipatingFinancialInstitution: values?.nonParticipatingFinancialInstitution?.toString() === '1' ? true : false,
        TrusteeDocumentedTrust: values?.trusteeDocumentedTrust?.toString() === '1' ? true : false,
        TrusteesTrusteeDocumentedTrust: values?.trusteesTrusteeDocumentedTrust,
        TrusteesGiinTrusteeDocumentedTrust: values?.trusteesGiinTrusteeDocumentedTrust?.replaceAll('-', '') ?? '',
        ExemptBeneficialOwner: values?.exemptBeneficialOwner?.toString() === '1' ? true : false,
        GovernmentalEntityForExemptEntities: values?.governmentalEntityForExemptEntities?.toString() === '1' ? true : false,
        CentralBankOfIssueForExemptEntities: values?.centralBankOfIssueForExemptEntities?.toString() === '1' ? true : false,
        InternationalOrganizationForExemptEntities: values?.internationalOrganizationForExemptEntities?.toString() === '1' ? true : false,
        NonReportingFfiForExemptEntities: values?.nonReportingFfiForExemptEntities?.toString() === '1' ? true : false,
        InvestmentEntityWhollyOwnedByExemptBeneficialOwners:
          values?.investmentEntityWhollyOwnedByExemptBeneficialOwners?.toString() === '1' ? true : false,
        ExceptedNonFinancialUsEntity: values?.exceptedNonFinancialUsEntity?.toString() === '1' ? true : false,
        ActiveNonFinancialNonUsEntity: values?.activeNonFinancialNonUsEntity?.toString() === '1' ? true : false,
        DirectOrSponsoredReportingNonFinancialForeignEntity:
          values?.directOrSponsoredReportingNonFinancialForeignEntity?.toString() === '1' ? true : false,
        GiinDirectOrSponsoredReportingNonFinancialForeignEntity:
          values?.giinDirectOrSponsoredReportingNonFinancialForeignEntity?.replaceAll('-', '') ?? '',
        PassiveNonFinancialNonUsEntity: values?.passiveNonFinancialNonUsEntity?.toString() === '1' ? true : false,
        NonReportingFinancialInstitutionUnderCrs: values?.nonReportingFinancialInstitutionUnderCrs?.toString() === '1' ? true : false,
        GovernmentalEntityForNonReportingFinancialInstitution:
          values?.governmentalEntityForNonReportingFinancialInstitution?.toString() === '1' ? true : false,
        InternationalOrganizationForNonReportingFinancialInstitution:
          values?.internationalOrganizationForNonReportingFinancialInstitution?.toString() === '1' ? true : false,
        CentralBankForNonReportingFinancialInstitution: values?.centralBankForNonReportingFinancialInstitution?.toString() === '1' ? true : false,
        BoardParticipationRetirementFund: values?.boardParticipationRetirementFund?.toString() === '1' ? true : false,
        narrowParticipationRetirementFund: values?.narrowParticipationRetirementFund?.toString() === '1' ? true : false,
        PensionFundOfGovtEntIntlOrgCentralBank: values?.pensionFundOfGovtEntIntlOrgCentralBank?.toString() === '1' ? true : false,
        ExemptCollectiveInvestmentVehicle: values?.exemptCollectiveInvestmentVehicle?.toString() === '1' ? true : false,
        TrusteeReportsAllRequiredCRSInfoForReportableAccounts:
          values?.trusteeReportsAllRequiredCRSInfoForReportableAccounts?.toString() === '1' ? true : false,
        QualifiedCreditCardIssuersForNonReportingFinancialInstitution:
          values?.qualifiedCreditCardIssuersForNonReportingFinancialInstitution?.toString() === '1' ? true : false,
        IsOtherEntityLowRiskUnderDomesticLaw: values?.isOtherEntityLowRiskUnderDomesticLaw?.toString() === '1' ? true : false,
        SpecifyTypeProvidedInDomesticLaw: values?.specifyTypeProvidedInDomesticLaw,
        PassiveNfe: values?.passiveNfe?.toString() === '1' ? true : false,
        IsActiveNfeRegularlyTradedCorporation: values?.isActiveNfeRegularlyTradedCorporation?.toString() === '1' ? true : false,
        NameOfSecurityMarkets: values?.nameOfSecurityMarkets,
        NameOfActiveNfeRelatedEntityOfRegTradedCorp: values?.nameOfActiveNfeRelatedEntityOfRegTradedCorp,
        ActiveNfeAGovernmentalEntity: values?.activeNfeAGovernmentalEntity?.toString() === '1' ? true : false,
        ActiveNfeCentralBank: values?.activeNfeCentralBank?.toString() === '1' ? true : false,
        ActiveNfeAnInternationalOrganization: values?.activeNfeAnInternationalOrganization?.toString() === '1' ? true : false,
        IsWhollyOwnedByClassifiedNFE: values?.isWhollyOwnedByClassifiedNFE?.toString() === '1' ? true : false,
        ActiveNfeOtherThan15Through17Above: values?.activeNfeOtherThan15Through17Above?.toString() === '1' ? true : false,
        DateOfIncorporation: values?.dateOfIncorporation,
        ActiveNfeReasonOfIncomeAndAssets: values?.activeNfeReasonOfIncomeAndAssets?.toString() === '1' ? true : false,
        HoldingNfeMemberofNonFinancialGroup: values?.holdingNfeMemberofNonFinancialGroup?.toString() === '1' ? true : false,
        TreasuryCenterNfeMemberofNonFinancialGroup: values?.treasuryCenterNfeMemberofNonFinancialGroup?.toString() === '1' ? true : false,
        startupNfeDateOfOrg: values?.startupNfeDateOfOrg?.toString() === '1' ? true : false,
        dateOfOrganization: values?.dateOfOrganization,
        NfeLiquidatingEmergingFromBankruptcy: values?.nfeLiquidatingEmergingFromBankruptcy?.toString() === '1' ? true : false,
        NonProfitNfe: values?.nonProfitNfe?.toString() === '1' ? true : false,
        OtherProvideReasonForThisSelection: values?.otherProvideReasonForThisSelection?.toString() === '1' ? true : false,
        activeNFEOtherReason: values?.activeNFEOtherReason ?? '',
        NonReportingFfiProvideTheType: values?.nonReportingFfiProvideTheType?.toString() === '1' ? true : false,
      },
    };
    dispatch(
      is_kyc ?
        saveKycTaxCertification(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        }) :
        saveTaxSelfCertification(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  const optionsYesOrNO = [
    { id: '1', name: getContent('yes') },
    { id: '0', name: getContent('no') },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-pick dropdown objects from the refetched list so labels show in the new language.
          country: findCountry(draftValues.current?.country?.id),
          currentResidenceCountry: findCountry(draftValues.current?.currentResidenceCountry?.id),
          mailingcountry: findCountry(draftValues.current?.mailingcountry?.id),
          countryOfControllingPersons: findCountry(draftValues.current?.countryOfControllingPersons?.id),
        } : {
          isAgreeFatcaAndCrs: tax_self_certification_data?.inputs?.isAgreeFatcaAndCrs ?? false,
          fullLegalNameOfTheEntity: tax_self_certification_data?.inputs?.fullLegalNameOfTheEntity,
          country: general_information_dropdowns?.countries?.find((item) => item?.id === tax_self_certification_data?.inputs?.countryId) || null,
          city: tax_self_certification_data?.inputs?.city,
          currentResidenceCountry:
            general_information_dropdowns?.countries?.find((item) => item?.id === tax_self_certification_data?.inputs?.currentResidenceCountryId) ||
            null,
          currentResidenceCity: tax_self_certification_data?.inputs?.currentResidenceCity,
          currentResidenceDistrict: tax_self_certification_data?.inputs?.currentResidenceDistrict,
          currentResidenceBuildingNumber: tax_self_certification_data?.inputs?.currentResidenceBuildingNumber,
          currentResidenceStreetName: tax_self_certification_data?.inputs?.currentResidenceStreetName,
          currentResidencePostalCode: tax_self_certification_data?.inputs?.currentResidencePostalCode,
          mailingcountry:
            general_information_dropdowns?.countries?.find(
              (item) => item?.id === tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.countryId,
            ) || null,
          mailingcity: tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.city ?? '',
          mailingdistrict: tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.district ?? '',
          mailingbuildingNumber:
            tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.buildingNumber ?? '',
          mailingstreetName: tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.streetName ?? '',
          mailingpostalCode: tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationOfficeAddressesList?.[0]?.postalCode ?? '',
          taxresidencetaxIdentificationNumber:
            tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationTaxResidenceInformationList?.[0]?.taxIdentificationNumber ?? '',
          taxresidenceCountryJurisdiction:
            tax_self_certification_data?.inputs?.corpInternationalTaxSelfCertificationTaxResidenceInformationList?.[0]?.countryJurisdiction ?? '',
          taxResidenceNoTinReasonId: tax_self_certification_data?.inputs?.taxResidenceNoTinReasonId
            ? [
              {
                valueId: tax_self_certification_data?.inputs?.taxResidenceNoTinReasonId,
              },
            ]
            : [],
          taxResidenceInfoReasonForNoTin: tax_self_certification_data?.inputs?.taxResidenceInfoReasonForNoTin ?? '',
          fatcaClassificationTinId: tax_self_certification_data?.inputs?.fatcaClassificationTinId,
          fatcaClassificationIsSpecifiedUsPerson: tax_self_certification_data?.inputs?.fatcaClassificationIsSpecifiedUsPerson === true ? 1 : 0,
          usPersonWhoIsNotASpecifiedUsPerson: tax_self_certification_data?.inputs?.usPersonWhoIsNotASpecifiedUsPerson === true ? 1 : 0,
          crsClassificationId: tax_self_certification_data?.inputs?.crsClassificationId,
          depositoryInstitution: tax_self_certification_data?.inputs?.depositoryInstitution === true ? 1 : 0,
          custodialInstitution: tax_self_certification_data?.inputs?.custodialInstitution === true ? 1 : 0,
          specifiedInsuranceCompany: tax_self_certification_data?.inputs?.specifiedInsuranceCompany === true ? 1 : 0,
          anInvestmentEntityGrossIncomePrimarily: tax_self_certification_data?.inputs?.anInvestmentEntityGrossIncomePrimarily === true ? '1' : '0',
          investmentEntityTypes: tax_self_certification_data?.inputs?.anInvestmentEntityTypeA
            ? '1'
            : tax_self_certification_data?.inputs?.anInvestmentEntityTypeB
              ? '0'
              : null,
          financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry:
            tax_self_certification_data?.inputs?.financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry === true ? 1 : 0,
          firstNameOfControllingPersons: tax_self_certification_data?.inputs?.firstNameOfControllingPersons,
          middleNameOfControllingPersons: tax_self_certification_data?.inputs?.middleNameOfControllingPersons,
          lastNameOfControllingPersons: tax_self_certification_data?.inputs?.lastNameOfControllingPersons,
          crsClassificationAddressOfControllingPerson: tax_self_certification_data?.inputs?.crsClassificationAddressOfControllingPerson,
          cityOfControllingPersons: tax_self_certification_data?.inputs?.cityOfControllingPersons,
          countryOfControllingPersons:
            general_information_dropdowns?.countries?.find(
              (item) => item?.id === tax_self_certification_data?.inputs?.countryOfControllingPersonsId,
            ) || null,
          districtOfControllingPersons: tax_self_certification_data?.inputs?.districtOfControllingPersons,
          buildingNumberOfControllingPersons: tax_self_certification_data?.inputs?.buildingNumberOfControllingPersons,
          streetNameOfControllingPersons: tax_self_certification_data?.inputs?.streetNameOfControllingPersons,
          mailingAddressOfControllingPersons: tax_self_certification_data?.inputs?.mailingAddressOfControllingPersons,
          crsClassificationDateOfBirth: tax_self_certification_data?.inputs?.crsClassificationDateOfBirth,
          crsClassificationNationalityOfControllingPerson: tax_self_certification_data?.inputs?.crsClassificationNationalityOfControllingPerson,
          crsClassificationPlaceOfBirth: tax_self_certification_data?.inputs?.crsClassificationPlaceOfBirth,
          crsClassificationPercentageOfOwnerShip: tax_self_certification_data?.inputs?.crsClassificationPercentageOfOwnerShip,
          countryOfTaxResidenceOfControllingPersons: tax_self_certification_data?.inputs?.countryOfTaxResidenceOfControllingPersons,
          zipcodeOfControllingPersons: tax_self_certification_data?.inputs?.zipcodeOfControllingPersons,
          additionalNoOfControllingPersons: tax_self_certification_data?.inputs?.additionalNoOfControllingPersons,
          tinForControllingPerson: tax_self_certification_data?.inputs?.tinForControllingPerson,
          unavailbleTINControllingPersonReasonId: tax_self_certification_data?.inputs?.unavailbleTINControllingPersonReasonId
            ? [{ valueId: tax_self_certification_data?.inputs?.unavailbleTINControllingPersonReasonId }]
            : [],
          reasonForTINUnavailableForControllingPerson: tax_self_certification_data?.inputs?.reasonForTINUnavailableForControllingPerson,
          controllingPersonStatusId: tax_self_certification_data?.inputs?.controllingPersonStatusId
            ? [
              {
                valueId: tax_self_certification_data?.inputs?.controllingPersonStatusId,
              },
            ]
            : [],
          isConfirmTheAboveInformation: tax_self_certification_data?.inputs?.isConfirmTheAboveInformation ?? false,
          fatcaClassificationUsTinNumber: tax_self_certification_data?.inputs?.fatcaClassificationUsTinNumber ?? '',
          ksaFinancialInstitution: tax_self_certification_data?.inputs?.ksaFinancialInstitution === true ? 1 : 0,
          giinKsaFinancialInstitution: formatGIIN(tax_self_certification_data?.inputs?.giinKsaFinancialInstitution),
          reasonProvidedForNotHavingGiin: tax_self_certification_data?.inputs?.reasonProvidedForNotHavingGiin ?? '',
          participatingFinancialInstitution: tax_self_certification_data?.inputs?.participatingFinancialInstitution === true ? 1 : 0,
          giinParticipatingFinancialInstitution: formatGIIN(tax_self_certification_data?.inputs?.giinParticipatingFinancialInstitution),
          registeredDeemedCompliantFinancialInstitution:
            tax_self_certification_data?.inputs?.registeredDeemedCompliantFinancialInstitution === true ? 1 : 0,
          localFfi: tax_self_certification_data?.inputs?.localFfi === true ? 1 : 0,
          nonReportingMemberPffiGroup: tax_self_certification_data?.inputs?.nonReportingMemberPffiGroup === true ? 1 : 0,
          qualifiedCollectiveInvestmentVehicles: tax_self_certification_data?.inputs?.qualifiedCollectiveInvestmentVehicles === true ? 1 : 0,
          restrictedFunds: tax_self_certification_data?.inputs?.restrictedFunds === true ? 1 : 0,
          qualifiedCreditCardIssuers: tax_self_certification_data?.inputs?.qualifiedCreditCardIssuers === true ? 1 : 0,
          sponsoredInvestmentEntities: tax_self_certification_data?.inputs?.sponsoredInvestmentEntities === true ? 1 : 0,
          sponsoredControlledForeignCorporation: tax_self_certification_data?.inputs?.sponsoredControlledForeignCorporation === true ? 1 : 0,
          giinSponsoredControlledForeignCorporation: formatGIIN(tax_self_certification_data?.inputs?.giinSponsoredControlledForeignCorporation),
          certifiedDeemedCompliantFinancialInstitution:
            tax_self_certification_data?.inputs?.certifiedDeemedCompliantFinancialInstitution === true ? 1 : 0,
          nonRegisteringLocalBank: tax_self_certification_data?.inputs?.nonRegisteringLocalBank === true ? 1 : 0,
          ffiWithOnlyLowValueAccounts: tax_self_certification_data?.inputs?.ffiWithOnlyLowValueAccounts === true ? 1 : 0,
          sponsoredCloselyHeldInvestmentVehicles: tax_self_certification_data?.inputs?.sponsoredCloselyHeldInvestmentVehicles === true ? 1 : 0,
          limitedLifeDebtInvestmentEntities: tax_self_certification_data?.inputs?.limitedLifeDebtInvestmentEntities === true ? 1 : 0,
          investmentAdvisorsAndInvestmentManagers: tax_self_certification_data?.inputs?.investmentAdvisorsAndInvestmentManagers === true ? 1 : 0,
          ownerDocumentedFfis: tax_self_certification_data?.inputs?.ownerDocumentedFfis === true ? 1 : 0,
          nonReportingFfiProvideTheType: tax_self_certification_data?.inputs?.nonReportingFfiProvideTheType === true ? 1 : 0,
          nonParticipatingFinancialInstitution: tax_self_certification_data?.inputs?.nonParticipatingFinancialInstitution === true ? 1 : 0,
          trusteeDocumentedTrust: tax_self_certification_data?.inputs?.trusteeDocumentedTrust === true ? 1 : 0,
          trusteesTrusteeDocumentedTrust: tax_self_certification_data?.inputs?.trusteesTrusteeDocumentedTrust ?? '',
          trusteesGiinTrusteeDocumentedTrust: formatGIIN(tax_self_certification_data?.inputs?.trusteesGiinTrusteeDocumentedTrust),
          nonReportingFinancialInstitutionUnderCrs: tax_self_certification_data?.inputs?.nonReportingFinancialInstitutionUnderCrs === true ? 1 : 0,
          governmentalEntityForNonReportingFinancialInstitution:
            tax_self_certification_data?.inputs?.governmentalEntityForNonReportingFinancialInstitution === true ? 1 : 0,
          internationalOrganizationForNonReportingFinancialInstitution:
            tax_self_certification_data?.inputs?.internationalOrganizationForNonReportingFinancialInstitution === true ? 1 : 0,
          centralBankForNonReportingFinancialInstitution:
            tax_self_certification_data?.inputs?.centralBankForNonReportingFinancialInstitution === true ? 1 : 0,
          boardParticipationRetirementFund: tax_self_certification_data?.inputs?.boardParticipationRetirementFund === true ? 1 : 0,
          narrowParticipationRetirementFund: tax_self_certification_data?.inputs?.narrowParticipationRetirementFund === true ? 1 : 0,
          pensionFundOfGovtEntIntlOrgCentralBank: tax_self_certification_data?.inputs?.pensionFundOfGovtEntIntlOrgCentralBank === true ? 1 : 0,
          exemptCollectiveInvestmentVehicle: tax_self_certification_data?.inputs?.exemptCollectiveInvestmentVehicle === true ? 1 : 0,
          trusteeReportsAllRequiredCRSInfoForReportableAccounts:
            tax_self_certification_data?.inputs?.trusteeReportsAllRequiredCRSInfoForReportableAccounts === true ? 1 : 0,
          qualifiedCreditCardIssuersForNonReportingFinancialInstitution:
            tax_self_certification_data?.inputs?.qualifiedCreditCardIssuersForNonReportingFinancialInstitution === true ? 1 : 0,
          isOtherEntityLowRiskUnderDomesticLaw: tax_self_certification_data?.inputs?.isOtherEntityLowRiskUnderDomesticLaw === true ? 1 : 0,
          specifyTypeProvidedInDomesticLaw: tax_self_certification_data?.inputs?.specifyTypeProvidedInDomesticLaw ?? '',
          exemptBeneficialOwner: tax_self_certification_data?.inputs?.exemptBeneficialOwner === true ? 1 : 0,
          governmentalEntityForExemptEntities: tax_self_certification_data?.inputs?.governmentalEntityForExemptEntities === true ? 1 : 0,
          centralBankOfIssueForExemptEntities: tax_self_certification_data?.inputs?.centralBankOfIssueForExemptEntities === true ? 1 : 0,
          internationalOrganizationForExemptEntities:
            tax_self_certification_data?.inputs?.internationalOrganizationForExemptEntities === true ? 1 : 0,
          nonReportingFfiForExemptEntities: tax_self_certification_data?.inputs?.nonReportingFfiForExemptEntities === true ? 1 : 0,
          investmentEntityWhollyOwnedByExemptBeneficialOwners:
            tax_self_certification_data?.inputs?.investmentEntityWhollyOwnedByExemptBeneficialOwners === true ? 1 : 0,
          exceptedNonFinancialUsEntity: tax_self_certification_data?.inputs?.exceptedNonFinancialUsEntity === true ? 1 : 0,
          activeNonFinancialNonUsEntity: tax_self_certification_data?.inputs?.activeNonFinancialNonUsEntity === true ? 1 : 0,
          directOrSponsoredReportingNonFinancialForeignEntity:
            tax_self_certification_data?.inputs?.directOrSponsoredReportingNonFinancialForeignEntity === true ? 1 : 0,
          giinDirectOrSponsoredReportingNonFinancialForeignEntity:
            formatGIIN(tax_self_certification_data?.inputs?.giinDirectOrSponsoredReportingNonFinancialForeignEntity),
          passiveNonFinancialNonUsEntity: tax_self_certification_data?.inputs?.passiveNonFinancialNonUsEntity === true ? 1 : 0,
          isActiveNfeRegularlyTradedCorporation: tax_self_certification_data?.inputs?.isActiveNfeRegularlyTradedCorporation === true ? 1 : 0,
          nameOfSecurityMarkets: tax_self_certification_data?.inputs?.nameOfSecurityMarkets ?? '',
          affiliatedEntityOfTradedCorporation: tax_self_certification_data?.inputs?.affiliatedEntityOfTradedCorporation ?? '',
          nameOfActiveNfeRelatedEntityOfRegTradedCorp: tax_self_certification_data?.inputs?.nameOfActiveNfeRelatedEntityOfRegTradedCorp ?? '',
          activeNfeAGovernmentalEntity: tax_self_certification_data?.inputs?.activeNfeAGovernmentalEntity === true ? 1 : 0,
          activeNfeCentralBank: tax_self_certification_data?.inputs?.activeNfeCentralBank === true ? 1 : 0,
          activeNfeAnInternationalOrganization: tax_self_certification_data?.inputs?.activeNfeAnInternationalOrganization === true ? 1 : 0,
          dateOfIncorporation: tax_self_certification_data?.inputs?.dateOfIncorporation ?? '',
          isWhollyOwnedByClassifiedNFE: tax_self_certification_data?.inputs?.isWhollyOwnedByClassifiedNFE === true ? 1 : 0,
          activeNfeOtherThan15Through17Above: tax_self_certification_data?.inputs?.activeNfeOtherThan15Through17Above === true ? 1 : 0,
          activeNfeReasonOfIncomeAndAssets: tax_self_certification_data?.inputs?.activeNfeReasonOfIncomeAndAssets === true ? 1 : 0,
          holdingNfeMemberofNonFinancialGroup: tax_self_certification_data?.inputs?.holdingNfeMemberofNonFinancialGroup === true ? 1 : 0,
          treasuryCenterNfeMemberofNonFinancialGroup:
            tax_self_certification_data?.inputs?.treasuryCenterNfeMemberofNonFinancialGroup === true ? 1 : 0,
          startupNfeDateOfOrg: tax_self_certification_data?.inputs?.startupNfeDateOfOrg === true ? 1 : 0,
          dateOfOrganization: tax_self_certification_data?.inputs?.dateOfOrganization,
          nonProfitNfe: tax_self_certification_data?.inputs?.nonProfitNfe === true ? 1 : 0,
          nfeLiquidatingEmergingFromBankruptcy: tax_self_certification_data?.inputs?.nfeLiquidatingEmergingFromBankruptcy === true ? 1 : 0,
          otherProvideReasonForThisSelection: tax_self_certification_data?.inputs?.otherProvideReasonForThisSelection === true ? 1 : 0,
          passiveNfe: tax_self_certification_data?.inputs?.passiveNfe === true ? 1 : 0,
          activeNFEOtherReason: tax_self_certification_data?.inputs?.activeNFEOtherReason ?? '',
          isSaveExist: false,
        }}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={international_tax_self_certification_validation_schema}
      >
        {({ handleSubmit, setFieldValue, values, setFieldError, errors, setFieldTouched, touched }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='w-full'>
                <CustomCheckbox
                  isChecked={values?.isAgreeFatcaAndCrs}
                  label={getContent('understand_and_agree')}
                  secondText={getContent('agree_self_certification_fatca_crs')}
                  onClick={() => {
                    setFieldValue('isAgreeFatcaAndCrs', !values?.isAgreeFatcaAndCrs);
                    setFieldTouched('isAgreeFatcaAndCrs', false);
                  }}
                  onPressSecondText={() => window.open('/pdfs/corporarte_declarations.pdf', '_blank')}
                />
              </RowView>
              <ErrorText text={getContent(touched?.isAgreeFatcaAndCrs ? errors?.isAgreeFatcaAndCrs : '')} className='block' />
              <div className='pt-3 sm:pt-5'>
                <Text
                  text={getContent('account_holder_information_heading')}
                  size={fontSizes.h22}
                  isTruncate={false}
                  weight={TextWeight.semiBold}
                  className={'block'}
                />
              </div>
              <div className='pt-3 sm:pt-5'>
                <Text
                  text={getContent('legal_name_entity_branch_heading')}
                  size={fontSizes.h18}
                  isTruncate={false}
                  weight={TextWeight.medium}
                  className={'block'}
                />
              </div>
              <RowView className='pt-3 sm:pt-5 w-full'>
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('legal_name_entity_branch')}
                  value={values?.fullLegalNameOfTheEntity}
                  onChange={(e) => {
                    setFieldValue('fullLegalNameOfTheEntity', e.target.value);
                    setFieldTouched('fullLegalNameOfTheEntity', true, false);
                  }}
                  labelDivClass={"h-9 sm:h-6"}
                  placeholder={getContent('placeholder_legal_name_entity_branch')}
                  errorMessage={getContent(touched?.fullLegalNameOfTheEntity ? errors?.fullLegalNameOfTheEntity : '')}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <Text
                  text={getContent('country_incorporation_establishment_heading')}
                  size={fontSizes.h18}
                  isTruncate={false}
                  weight={TextWeight.medium}
                  className={'block'}
                />
              </div>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country')}
                  placeholder={getContent('placeholder_country')}
                  value={values?.country}
                  options={general_information_dropdowns?.countries}
                  selectedValue={values?.country}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('country', val);
                    setFieldTouched('country', false);
                  }}
                  errorMessage={getContent(touched?.country ? errors?.country : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('city')}
                  value={values?.city}
                  onChange={(e) => {
                    setFieldValue('city', e.target.value);
                    setFieldTouched('city', true, false);
                  }}
                  placeholder={getContent('placeholder_city')}
                  errorMessage={getContent(touched?.city ? errors?.city : '')}
                  maxLength={20}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('current_residence_address_heading')} size={fontSizes.h18} isTruncate={false} weight={TextWeight.medium} className={'block'} />
              </div>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country')}
                  placeholder={getContent('placeholder_country')}
                  value={values?.currentResidenceCountry}
                  options={general_information_dropdowns?.countries}
                  selectedValue={values?.currentResidenceCountry}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('currentResidenceCountry', val);
                    setFieldTouched('currentResidenceCountry', false);
                  }}
                  errorMessage={getContent(touched?.currentResidenceCountry ? errors?.currentResidenceCountry : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('city')}
                  value={values?.currentResidenceCity}
                  onChange={(e) => {
                    setFieldValue('currentResidenceCity', e.target.value);
                    setFieldTouched('currentResidenceCity', true, false);
                  }}
                  placeholder={getContent('placeholder_city')}
                  errorMessage={getContent(touched?.currentResidenceCity ? errors?.currentResidenceCity : '')}
                  maxLength={20}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('district')}
                  value={values?.currentResidenceDistrict}
                  onChange={(e) => {
                    setFieldValue('currentResidenceDistrict', e.target.value);
                    setFieldTouched('currentResidenceDistrict', true, false);
                  }}
                  placeholder={getContent('placeholder_district')}
                  errorMessage={getContent(touched?.currentResidenceDistrict ? errors?.currentResidenceDistrict : '')}
                />
              </RowView>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('building_number')}
                  isRounded
                  isRequired
                  value={values?.currentResidenceBuildingNumber}
                  onChange={(e) => {
                    setFieldValue('currentResidenceBuildingNumber', e.target.value);
                    setFieldTouched('currentResidenceBuildingNumber', true, false);
                  }}
                  labelDivClass={"h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_building_number')}
                  errorMessage={getContent(touched?.currentResidenceBuildingNumber ? errors?.currentResidenceBuildingNumber : '')}
                />
                <CustomInput
                  label={getContent('street_name')}
                  isRequired
                  isRounded
                  value={values?.currentResidenceStreetName}
                  onChange={(e) => {
                    setFieldValue('currentResidenceStreetName', e.target.value);
                    setFieldTouched('currentResidenceStreetName', true, false);
                  }}
                  labelDivClass={"h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_street_name')}
                  errorMessage={getContent(touched?.currentResidenceStreetName ? errors?.currentResidenceStreetName : '')}
                />

                <CustomInput
                  label={getContent('postal_code')}
                  isRequired
                  isRounded
                  value={values?.currentResidencePostalCode}
                  onChange={(e) => {
                    setFieldValue('currentResidencePostalCode', e.target.value);
                    setFieldTouched('currentResidencePostalCode', true, false);
                  }}
                  labelDivClass={"h-10 sm:h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_postal_code')}
                  maxLength={10}
                  errorMessage={getContent(touched?.currentResidencePostalCode ? errors?.currentResidencePostalCode : '')}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('mailing_address_if_different_heading')} size={fontSizes.h18} isTruncate={false} weight={TextWeight.medium} className={'block'} />
              </div>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  label={getContent('country')}
                  placeholder={getContent('placeholder_country')}
                  options={general_information_dropdowns?.countries}
                  selectedValue={values?.mailingcountry}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('mailingcountry', val);
                    setFieldTouched('mailingcountry', false);
                  }}
                  errorMessage={getContent(touched?.mailingcountry ? errors?.mailingcountry : '')}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('city')}
                  value={values?.mailingcity}
                  onChange={(e) => {
                    setFieldValue('mailingcity', e.target.value);
                    setFieldTouched('mailingcity', true, false);
                  }}
                  placeholder={getContent('placeholder_city')}
                  errorMessage={getContent(touched?.mailingcity ? errors?.mailingcity : '')}
                  maxLength={20}
                />
                <CustomInput
                  isRounded
                  isRequired={true}
                  label={getContent('district')}
                  value={values?.mailingdistrict}
                  onChange={(e) => {
                    setFieldValue('mailingdistrict', e.target.value);
                    setFieldTouched('mailingdistrict', true, false);
                  }}
                  placeholder={getContent('placeholder_district')}
                  errorMessage={getContent(touched?.mailingdistrict ? errors?.mailingdistrict : '')}
                />
              </RowView>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('building_number')}
                  isRequired
                  isRounded
                  value={values?.mailingbuildingNumber}
                  onChange={(e) => {
                    setFieldValue('mailingbuildingNumber', e.target.value);
                    setFieldTouched('mailingbuildingNumber', true, false);
                  }}
                  labelDivClass={"h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_building_number')}
                  errorMessage={getContent(touched?.mailingbuildingNumber ? errors?.mailingbuildingNumber : '')}
                />
                <CustomInput
                  label={getContent('street_name')}
                  isRequired
                  isRounded
                  value={values?.mailingstreetName}
                  onChange={(e) => {
                    setFieldValue('mailingstreetName', e.target.value);
                    setFieldTouched('mailingstreetName', true, false);
                  }}
                  labelDivClass={"h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_street_name')}
                  errorMessage={getContent(touched?.mailingstreetName ? errors?.mailingstreetName : '')}
                />
                <CustomInput
                  label={getContent('postal_code')}
                  isRequired
                  isRounded
                  value={values?.mailingpostalCode}
                  onChange={(e) => {
                    setFieldValue('mailingpostalCode', e.target.value);
                    setFieldTouched('mailingpostalCode', true, false);
                  }}
                  labelDivClass={"h-10 sm:h-6 md:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_postal_code')}
                  maxLength={10}
                  errorMessage={getContent(touched?.mailingpostalCode ? errors?.mailingpostalCode : '')}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('tax_residency_information_heading')} size={fontSizes.h22} isTruncate={false} weight={TextWeight.semiBold} className={'block'} />
              </div>
              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('country_or_jurisdiction_heading')} size={fontSizes.h16} isTruncate={false} weight={TextWeight.medium} className={'block'} />
              </div>
              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('tax_residence_country_or_jurisdiction')}
                  isRounded
                  value={values?.taxresidenceCountryJurisdiction}
                  onChange={(e) => {
                    setFieldValue('taxresidenceCountryJurisdiction', e.target.value);
                    setFieldTouched('taxresidenceCountryJurisdiction', true, false);
                  }}
                  labelDivClass={"h-10 sm:h-6 md:h-9 lg:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_tax_residence_country_or_jurisdiction')}
                  errorMessage={getContent(touched?.taxresidenceCountryJurisdiction ? errors?.taxresidenceCountryJurisdiction : '')}
                />
                <CustomInput
                  label={getContent('taxpayer_identification_number')}
                  isRequired
                  isRounded
                  value={values?.taxresidencetaxIdentificationNumber}
                  onChange={(e) => {
                    setFieldTouched('taxresidencetaxIdentificationNumber', true, false);
                    setFieldValue('taxResidenceNoTinReasonId', []);
                    setFieldValue('taxresidencetaxIdentificationNumber', e.target.value);
                  }}
                  maxLength={15}
                  labelDivClass={"h-12 sm:h-6 md:h-9 lg:h-10 xl:h-6"}
                  placeholder={getContent('placeholder_number')}
                  errorMessage={getContent(touched?.taxresidencetaxIdentificationNumber ? errors?.taxresidencetaxIdentificationNumber : '')}
                />
              </RowView>
              {
                values?.taxresidencetaxIdentificationNumber?.trim()?.length > 0
                  ? null
                  :
                  <>
                    <div className='pt-3 sm:pt-5'>
                      <Checkbox
                        label={getContent('tin_unavailability_reason')}
                        value={values.taxResidenceNoTinReasonId}
                        onChange={(option) => {
                          const alreadySelected =
                            values?.taxResidenceNoTinReasonId?.[0]?.valueId === option?.id;

                          if (alreadySelected) {
                            setFieldValue('taxResidenceNoTinReasonId', []);
                          } else {
                            setFieldValue('taxResidenceNoTinReasonId', [
                              { valueId: option?.id },
                            ]);
                          }
                          setFieldTouched('taxResidenceNoTinReasonId', false, false);
                        }}
                        options={text_self_certification_dropdowns?.dropDownFatcaTaxResidenceNoTin}
                        isVertical
                      />
                      <ErrorText text={getContent(touched?.taxResidenceNoTinReasonId ? errors?.taxResidenceNoTinReasonId : '')} className='block' />
                    </div>
                    {values?.taxResidenceNoTinReasonId?.[0]?.valueId === 1456 && (
                      <RowView className='pt-3 sm:pt-5'>
                        <CustomInput
                          isRounded
                          isRequired={true}
                          label={getContent('tax_residence_info_reason_for_no_tin')}
                          value={values?.taxResidenceInfoReasonForNoTin}
                          onChange={(e) => {
                            setFieldValue(
                              'taxResidenceInfoReasonForNoTin',
                              e.target.value
                            );
                            setFieldTouched('taxResidenceInfoReasonForNoTin', true, false);
                          }}
                          labelDivClass={"h-8 sm:h-6"}
                          placeholder={getContent('placeholder_tax_residence_info_reason_for_no_tin')}
                          errorMessage={getContent(touched?.taxResidenceInfoReasonForNoTin ? errors?.taxResidenceInfoReasonForNoTin : '')}
                          required={false}
                        />
                      </RowView>
                    )}
                  </>
              }
              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('fatca_Questionnaire')} size={fontSizes.h22} isTruncate={false} weight={TextWeight.semiBold} className={'block'} />
              </div>
              <div className='gap-7 pt-3 sm:pt-5'>
                <Chips
                  label={getContent('single_classification_selection')}
                  value={values?.fatcaClassificationTinId}
                  onChange={(e) => {
                    setFieldValue('fatcaClassificationTinId', e);
                    setFieldTouched('fatcaClassificationTinId', false);
                  }}
                  options={text_self_certification_dropdowns?.dropDownFatcaQuestionnaire}
                />
                <ErrorText text={getContent(touched?.fatcaClassificationTinId ? errors?.fatcaClassificationTinId : '')} className='block' />
              </div>

              {Number(values.fatcaClassificationTinId) === 1458 && (
                <UsEntitiesFinancialInstitutions
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              {Number(values.fatcaClassificationTinId) === 1459 && (
                <NonUsFinancialThen
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                  formatGIIN={formatGIIN}
                />
              )}

              {Number(values?.fatcaClassificationTinId) === 1460 && (
                <ExemptEntityThen
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              {Number(values?.fatcaClassificationTinId) === 1461 && (
                <NonUsNotFinancialThen
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                  formatGIIN={formatGIIN}
                />
              )}

              {Number(values?.fatcaClassificationTinId) === 1462 && (
                <PassiveNonUsEntity
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('crs_questionnaire_heading')} size={fontSizes.h22} isTruncate={false} weight={TextWeight.semiBold} className={'block'} />
              </div>
              <div className='pt-3 sm:pt-5'>
                <Chips
                  label={getContent('crs_questionnaire')}
                  value={values?.crsClassificationId}
                  onChange={(e) => {
                    setFieldValue('crsClassificationId', e);
                    setFieldTouched('crsClassificationId', false);
                  }}
                  options={text_self_certification_dropdowns?.dropDownCrsQuestionnaire}
                />
                <ErrorText text={getContent(touched?.crsClassificationId ? errors?.crsClassificationId : '')} className='block' />
              </div>
              {Number(values?.crsClassificationId) === 1463 && (
                <FIThen
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              {Number(values?.crsClassificationId) === 1539 && (
                <NonReportingFiThen
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              {Number(values?.crsClassificationId) === 1464 && (
                <ActiveNonFinancial
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                  isArabic={isArabic}
                />
              )}

              {Number(values?.crsClassificationId) === 1465 && (
                <PassiveNonFinancialEntity
                  getContent={getContent}
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  touched={touched}
                  optionsYesOrNO={optionsYesOrNO}
                  errors={errors}
                />
              )}

              {((Number(values?.crsClassificationId) === 1463 &&
                Number(values?.financialInstitutionInvestmentEntityLocatedInANonParticipatingCountry) === 1) ||
                (Number(values?.crsClassificationId) === 1465 && Number(values?.passiveNfe) === 1) ||
                (Number(values?.fatcaClassificationTinId) === 1462 && Number(values?.passiveNonFinancialNonUsEntity) === 1)) && (
                  <ControllingPerson
                    getContent={getContent}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    setFieldTouched={setFieldTouched}
                    touched={touched}
                    text_self_certification_dropdowns={text_self_certification_dropdowns}
                    general_information_dropdowns={general_information_dropdowns}
                    errors={errors}
                  />
                )}

              <div className='pt-3 sm:pt-5'>
                <Text text={getContent('declarations_heading')} size={fontSizes.h22} isTruncate={false} weight={TextWeight.semiBold} className={'block'} />
              </div>
              <div className='pt-1.5 sm:pt-3'>
                <Text
                  text={getContent('declarations_text')}
                  size={fontSizes.h14}
                  color='text-primary-dark'
                  isTruncate={false}
                  weight={TextWeight.regular}
                  className={'block'}
                />
              </div>

              <div className='pt-3 sm:pt-5'>
                <RowView className='gap-7 w-full'>
                  <CustomCheckbox
                    isChecked={values?.isConfirmTheAboveInformation}
                    label={getContent('confirm_read_information')}
                    className={'mt-4'}
                    onClick={() => {
                      setFieldValue('isConfirmTheAboveInformation', !values?.isConfirmTheAboveInformation);
                      setFieldTouched('isConfirmTheAboveInformation', false);
                    }}
                  />
                </RowView>
                <ErrorText text={getContent(touched?.isConfirmTheAboveInformation ? errors?.isConfirmTheAboveInformation : '')} className='block' />
              </div>
              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_and_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  loading={corporate_post_loading}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_and_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={corporate_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik >
    </>
  );
};

export default InternationalTaxSelfCertification;
